import React, { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { useApp } from '../../context/AppContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Dumbbell, AlertTriangle, RefreshCw, Clock, Lightbulb, ChevronRight, ChevronLeft, Target, Ruler } from 'lucide-react';
import { toast } from 'sonner';

// values stay stable for the API; labels are localized via i18n keys
const GOALS = [
  { v: 'build muscle', k: 'goalMuscle' },
  { v: 'lose fat', k: 'goalFat' },
  { v: 'improve endurance', k: 'goalEndurance' },
  { v: 'general fitness', k: 'goalFitness' },
  { v: 'increase strength', k: 'goalStrength' },
  { v: 'flexibility & mobility', k: 'goalFlex' },
];
const LEVELS = [
  { v: 'beginner', k: 'levelBeginner' },
  { v: 'intermediate', k: 'levelIntermediate' },
  { v: 'advanced', k: 'levelAdvanced' },
];
const EQUIPMENT = [
  { v: 'gym', k: 'eqGym' },
  { v: 'home', k: 'eqHome' },
  { v: 'dumbbells', k: 'eqDumbbells' },
  { v: 'barbell', k: 'eqBarbell' },
  { v: 'bodyweight only', k: 'eqBodyweight' },
  { v: 'resistance bands', k: 'eqBands' },
  { v: 'kettlebell', k: 'eqKettlebell' },
];

function Chip({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3.5 py-2 rounded-full text-sm border transition-colors min-h-[40px] ${
        active
          ? 'border-foreground bg-foreground text-background font-semibold'
          : 'border-border text-muted-foreground hover:border-foreground/40'
      }`}
    >
      {children}
    </button>
  );
}

export default function CoachTab() {
  const { t } = useApp();
  const [plan, setPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    goal: 'build muscle', level: 'beginner', equipment: ['home', 'dumbbells'],
    duration: 45, injuries: '', weight_kg: '', height_cm: '',
  });
  const [adaptDialog, setAdaptDialog] = useState(null);
  const [adaptDetails, setAdaptDetails] = useState('');
  const [adapting, setAdapting] = useState(false);

  useEffect(() => {
    api.get('/ai/coach/plan').then((r) => { setPlan(r.data); if (!r.data) setWizardOpen(true); }).catch(() => {}).finally(() => setLoadingPlan(false));
  }, []);

  const toggleEquip = (eq) => {
    setForm((f) => ({
      ...f,
      equipment: f.equipment.includes(eq) ? f.equipment.filter((x) => x !== eq) : [...f.equipment, eq],
    }));
  };

  const generate = async () => {
    if (form.equipment.length === 0) { toast.error(t('availableEquipment')); return; }
    setGenerating(true);
    try {
      const payload = {
        goal: form.goal, level: form.level, equipment: form.equipment,
        duration: Number(form.duration) || 45, injuries: form.injuries,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
        height_cm: form.height_cm ? Number(form.height_cm) : null,
      };
      const res = await api.post('/ai/coach/generate', payload);
      setPlan(res.data);
      setWizardOpen(false);
      setStep(0);
      toast.success('\u2713');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setGenerating(false); }
  };

  const adapt = async () => {
    setAdapting(true);
    try {
      const res = await api.post('/ai/coach/adapt', {
        day: adaptDialog.day,
        reason: adaptDialog.reason,
        details: adaptDetails,
      });
      setPlan((p) => ({
        ...p,
        plan: {
          ...p.plan,
          weekly_plan: p.plan.weekly_plan.map((d) => (d.day === adaptDialog.day ? res.data.adapted_day : d)),
        },
      }));
      toast.success(res.data.adapted_day.adaptation_note || '\u2713');
      setAdaptDialog(null); setAdaptDetails('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setAdapting(false); }
  };

  if (loadingPlan) {
    return <div className="py-12 text-center text-muted-foreground text-sm">{t('loading')}</div>;
  }

  const steps = [
    // ---- Step 1: Goal + Level ----
    <div key="s0" className="space-y-5">
      <div className="space-y-2">
        <Label className="text-xs flex items-center gap-1.5"><Target size={13} /> {t('goal')}</Label>
        <div className="flex flex-wrap gap-2" data-testid="ai-coach-goal-options">
          {GOALS.map((g) => (
            <Chip key={g.v} testid="ai-coach-goal-chip" active={form.goal === g.v} onClick={() => setForm({ ...form, goal: g.v })}>
              {t(g.k)}
            </Chip>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-xs">{t('level')}</Label>
        <div className="flex flex-wrap gap-2" data-testid="ai-coach-level-options">
          {LEVELS.map((l) => (
            <Chip key={l.v} testid="ai-coach-level-chip" active={form.level === l.v} onClick={() => setForm({ ...form, level: l.v })}>
              {t(l.k)}
            </Chip>
          ))}
        </div>
      </div>
    </div>,
    // ---- Step 2: Body + Equipment ----
    <div key="s1" className="space-y-5">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1.5"><Ruler size={13} /> {t('weight')}</Label>
          <Input data-testid="ai-coach-weight-input" type="number" inputMode="decimal" min={30} max={300} value={form.weight_kg}
            onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} placeholder="70" dir="ltr" className="text-start" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">{t('height')}</Label>
          <Input data-testid="ai-coach-height-input" type="number" inputMode="decimal" min={100} max={250} value={form.height_cm}
            onChange={(e) => setForm({ ...form, height_cm: e.target.value })} placeholder="175" dir="ltr" className="text-start" />
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-xs">{t('availableEquipment')}</Label>
        <div className="flex flex-wrap gap-2" data-testid="ai-coach-equipment-options">
          {EQUIPMENT.map((eq) => (
            <Chip key={eq.v} testid="ai-coach-equipment-chip" active={form.equipment.includes(eq.v)} onClick={() => toggleEquip(eq.v)}>
              {t(eq.k)}
            </Chip>
          ))}
        </div>
      </div>
    </div>,
    // ---- Step 3: Duration + Injuries ----
    <div key="s2" className="space-y-5">
      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1.5"><Clock size={13} /> {t('minutesPerDay')}</Label>
        <Input data-testid="ai-coach-duration-input" type="number" inputMode="numeric" min={15} max={120} value={form.duration}
          onChange={(e) => setForm({ ...form, duration: e.target.value })} dir="ltr" className="text-start" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs flex items-center gap-1.5"><AlertTriangle size={13} /> {t('injuriesOptional')}</Label>
        <Input data-testid="ai-coach-injuries-input" value={form.injuries} placeholder={t('whichArea')}
          onChange={(e) => setForm({ ...form, injuries: e.target.value })} />
      </div>
    </div>,
  ];

  return (
    <div className="space-y-5" data-testid="ai-coach-form">
      {/* Wizard card */}
      {wizardOpen ? (
        <div className="bg-card rounded-xl border border-border p-5">
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-semibold flex items-center gap-2 text-sm sm:text-base">
              <Dumbbell size={17} /> {t('createPlan')}
            </h3>
            {/* step dots */}
            <div className="flex gap-1.5" data-testid="ai-coach-wizard-steps">
              {[0, 1, 2].map((i) => (
                <span key={i} className={`h-1.5 rounded-full transition-colors duration-150 ${i === step ? 'w-6 bg-foreground' : 'w-1.5 bg-muted-foreground/40'}`} />
              ))}
            </div>
          </div>

          {steps[step]}

          <div className="flex gap-2 mt-6">
            {step > 0 && (
              <Button data-testid="ai-coach-wizard-back" variant="secondary" onClick={() => setStep(step - 1)} className="flex-1" disabled={generating}>
                <ChevronLeft size={15} className="me-1 rtl:rotate-180" /> {t('back')}
              </Button>
            )}
            {step < 2 ? (
              <Button data-testid="ai-coach-wizard-next" onClick={() => setStep(step + 1)} className="flex-1 font-semibold"
                disabled={step === 1 && form.equipment.length === 0}>
                {t('next')} <ChevronRight size={15} className="ms-1 rtl:rotate-180" />
              </Button>
            ) : (
              <Button data-testid="ai-coach-generate-plan-button" onClick={generate} disabled={generating} className="flex-1 font-semibold">
                {generating ? (
                  <span className="flex items-center gap-2"><RefreshCw size={15} className="animate-spin" /> {t('buildingPlan')}</span>
                ) : t('generatePlan')}
              </Button>
            )}
          </div>
        </div>
      ) : (
        <Button data-testid="ai-coach-open-wizard-button" variant="outline" onClick={() => { setWizardOpen(true); setStep(0); }} className="w-full">
          <RefreshCw size={14} className="me-2" /> {t('regeneratePlan')}
        </Button>
      )}

      {/* Plan viewer */}
      {!plan && !generating && !wizardOpen && (
        <div className="text-center py-8 text-muted-foreground text-sm">{t('noPlan')}</div>
      )}
      {plan && (
        <div className="bg-card rounded-xl border border-border p-4 sm:p-5" data-testid="ai-coach-plan-view">
          <div className="mb-4">
            <h3 className="font-semibold text-lg">{plan.plan.plan_name}</h3>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{plan.plan.summary}</p>
          </div>
          <Accordion type="single" collapsible className="space-y-2">
            {plan.plan.weekly_plan.map((day) => (
              <AccordionItem key={day.day} value={day.day} className="border border-border rounded-lg px-3">
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-start min-w-0">
                    <span className="text-sm font-semibold">{day.day}</span>
                    <span className="text-xs text-muted-foreground truncate">{day.focus}</span>
                    {day.is_rest ? (
                      <Badge variant="secondary" className="text-[10px]">{t('rest')}</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">
                        <Clock size={10} className="me-1" />{day.duration_minutes} {t('minUnit')}
                      </Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  {day.is_rest ? (
                    <p className="text-sm text-muted-foreground pb-3">{t('restDay')}</p>
                  ) : (
                    <div className="space-y-2 pb-3">
                      {day.exercises?.map((ex, i) => (
                        <div key={i} className="flex flex-wrap items-center justify-between gap-2 p-2.5 rounded-lg bg-secondary/50">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">{ex.name}</p>
                            <p className="text-[11px] text-muted-foreground">{ex.target_muscles?.join('\u060c ')}{ex.notes ? ` \u00b7 ${ex.notes}` : ''}</p>
                          </div>
                          <div className="text-end shrink-0">
                            <p className="text-sm font-semibold tabular-nums" dir="ltr">{ex.sets} × {ex.reps}</p>
                            <p className="text-[11px] text-muted-foreground tabular-nums">{t('rest')} {ex.rest_seconds}s</p>
                          </div>
                        </div>
                      ))}
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button data-testid="ai-coach-injury-button" size="sm" variant="outline" className="text-xs h-9 border-destructive/40 text-destructive hover:bg-destructive/10"
                          onClick={() => setAdaptDialog({ day: day.day, reason: 'injury' })}>
                          <AlertTriangle size={12} className="me-1.5" /> {t('injuryFlag')}
                        </Button>
                        <Button data-testid="ai-coach-too-difficult-button" size="sm" variant="outline" className="text-xs h-9"
                          onClick={() => setAdaptDialog({ day: day.day, reason: 'too_difficult' })}>
                          {t('tooDifficult')}
                        </Button>
                      </div>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {plan.plan.tips?.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-secondary/40 border border-border">
              <p className="text-xs font-semibold flex items-center gap-1.5 mb-2"><Lightbulb size={13} /> {t('coachTips')}</p>
              <ul className="text-xs text-muted-foreground space-y-1 list-disc ms-4 leading-relaxed">
                {plan.plan.tips.map((tip, i) => <li key={i}>{tip}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Adapt dialog */}
      <Dialog open={!!adaptDialog} onOpenChange={(v) => !v && setAdaptDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{adaptDialog?.reason === 'injury' ? t('injuryFlag') : t('tooDifficult')}</DialogTitle>
            <DialogDescription>{adaptDialog?.day}</DialogDescription>
          </DialogHeader>
          {adaptDialog?.reason === 'injury' && (
            <Input data-testid="ai-coach-injury-details-input" value={adaptDetails} onChange={(e) => setAdaptDetails(e.target.value)}
              placeholder={t('whichArea')} />
          )}
          <Button data-testid="ai-coach-adapt-confirm-button" onClick={adapt} disabled={adapting || (adaptDialog?.reason === 'injury' && !adaptDetails.trim())} className="w-full">
            {adapting ? t('loading') : t('adaptRoutine')}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
